import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import { boardCardsByAcceptance, formatWeekDayLabel, type WorkspaceOperations } from './workspaceDashboard.model';
import { OPERATIONAL_QUEUES } from './operationalState';
import { boardCardNext } from '../workspacesDashboardExport';

const TABLE_SX = {
  width: '100%',
  minWidth: 960,
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  '& th': {
    textAlign: 'left',
    fontSize: '0.75rem',
    color: 'neutral.500',
    py: 0.75,
    px: 1,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& td': { py: 1, px: 1, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' },
  '& th:first-of-type, & td:first-of-type': { pl: 0 },
  '& th:last-of-type, & td:last-of-type': { pr: 0, textAlign: 'right' },
} as const;

const QUEUE_LABEL = Object.fromEntries(OPERATIONAL_QUEUES.map((queue) => [queue.id, queue.label]));

/** Active workspaces that are Live Jobs (quote accepted) or still Pre-Sale. */
const WorkspaceDashboardJobs: React.FC<{ ops: WorkspaceOperations; accepted: boolean }> = ({ ops, accepted }) => {
  const rows = boardCardsByAcceptance(ops, accepted);
  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="title-lg">{accepted ? 'Live Job' : 'Pre-Sale'} ({rows.length})</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          {accepted
            ? 'Quote accepted and the workspace is not completed or cancelled.'
            : 'No quote accepted yet, and the workspace is not completed or cancelled.'}
          {' '}Date is the most advanced on file: install, then production, then final survey, then first survey.
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          {accepted ? 'No live jobs.' : 'Nothing in pre-sale.'}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={TABLE_SX}>
            <colgroup>
              <col style={{ width: 130 }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '13%' }} />
              {accepted && <col style={{ width: 110 }} />}
              <col style={{ width: '22%' }} />
              <col />
              <col style={{ width: 70 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Workspace</th>
                <th>Customer</th>
                {accepted && <th>Stage</th>}
                <th>Status</th>
                <th>Next</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.projectId}>
                  <td>
                    {row.keyDate ? (
                      <>
                        <Typography level="body-sm" sx={{ whiteSpace: 'nowrap' }}>{formatWeekDayLabel(row.keyDate.day)}</Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{row.keyDate.label}</Typography>
                      </>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>—</Typography>
                    )}
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      fontWeight="lg"
                      component={Link}
                      to={row.href}
                      sx={{ textDecoration: 'none', overflowWrap: 'anywhere' }}
                    >
                      {row.workspace}
                    </Typography>
                  </td>
                  <td><Typography level="body-sm">{row.customer}</Typography></td>
                  {accepted && <td><Typography level="body-sm">{QUEUE_LABEL[row.queue] || row.queue}</Typography></td>}
                  <td>
                    <Typography level="body-sm" sx={{ color: row.exception ? 'danger.600' : undefined }}>
                      {row.stateLabel}
                    </Typography>
                    {row.flags.map((flag) => (
                      <Chip key={flag} size="sm" variant="outlined" color="warning" sx={{ mt: 0.5 }}>{flag}</Chip>
                    ))}
                  </td>
                  <td>
                    {boardCardNext(row) ? (
                      <Typography level="body-sm">{boardCardNext(row)}</Typography>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>—</Typography>
                    )}
                  </td>
                  <td>
                    <Button size="sm" variant="plain" component={Link} to={row.href}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default WorkspaceDashboardJobs;
